import { useEffect, useState } from "react";
import { Clapperboard, PlugZap, Play, Film, Clock } from "lucide-react";
import api from "@/lib/api";
import { CinematicStage, StageHeader, GlassCard, RoomBackdrop, Pill, Spinner, EmptyState, cx } from "@/components/shared";
import { ROOM_BACKDROPS } from "@/lib/studio";

const FUTURE = ["Runway", "Google Veo", "Kling", "Luma", "Pika", "OpenAI Sora"];

export default function VideoEngine() {
  const [providers, setProviders] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/providers").then(({ data }) => setProviders(data)).catch(() => {});
    api.get("/projects").then(({ data }) => setProjects(data)).catch(() => {});
  }, []);

  if (!providers) return <Spinner label="Powering up the stage…" />;
  const configured = providers.video?.configured;

  return (
    <CinematicStage>
      <StageHeader
        stage="10"
        room="The Stage · Video Engine"
        icon={Clapperboard}
        title="Cinematic scene rendering"
        subtitle="A provider-agnostic rendering stage with a scene timeline, queue and per-shot retry."
        actions={<Pill tone={configured ? "green" : "slate"}><PlugZap className="w-3 h-3" /> {configured ? "Camera connected" : "Camera offline"}</Pill>}
      />

      {!configured && (
        <>
          <EmptyState
            image={ROOM_BACKDROPS.stage}
            icon={Clapperboard}
            title="Connect a video provider to roll cameras"
            subtitle="Your storyboard, character memory and cost model are already blocked out. Plug in a provider and rendering starts immediately — no code changes."
            action={<a href="/settings" className="px-5 py-2.5 rounded-xl bg-[#5B4DFF] text-white font-semibold inline-block">Manage Providers</a>}
          />

          <div className="mt-8">
            <div className="font-mono text-[11px] uppercase tracking-wider text-[#FFB300] mb-3">Swappable cameras (register in Settings)</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {FUTURE.map((f) => (
                <GlassCard key={f} hover className="p-4 text-center opacity-75 hover:opacity-100">
                  <Film className="w-5 h-5 mx-auto text-slate-400 mb-2" />
                  <div className="text-xs text-slate-300">{f}</div>
                </GlassCard>
              ))}
            </div>
          </div>
        </>
      )}

      <div className="mt-10">
        <h3 className="font-display text-lg font-semibold mb-4">Render Queue</h3>
        {projects.length === 0 ? (
          <GlassCard className="p-8 text-center text-slate-500">No productions on the stage yet.</GlassCard>
        ) : (
          <div className="space-y-3">
            {projects.map((p) => (
              <GlassCard key={p.id} className="p-4 flex items-center gap-4" data-testid="video-queue-item">
                <div className="w-12 h-12 rounded-xl bg-[#5B4DFF]/15 border border-[#5B4DFF]/25 grid place-items-center"><Clapperboard className="w-5 h-5 text-[#5B4DFF]" /></div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-medium truncate">{p.title}</div>
                  <div className="text-xs text-slate-500 font-mono">{p.config?.aspect_ratio} · {p.config?.quality} · {p.config?.duration}</div>
                </div>
                <Pill tone={configured ? "gold" : "slate"}><Clock className="w-3 h-3" /> {configured ? "Ready to render" : "Awaiting camera"}</Pill>
                <button disabled={!configured} className={cx("p-2 rounded-lg border", configured ? "bg-[#5B4DFF] border-[#5B4DFF] text-white" : "bg-white/5 border-white/10 text-slate-600")}><Play className="w-4 h-4" /></button>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </CinematicStage>
  );
}
